import React, { useRef, useState, useEffect, useCallback } from 'react';
import { clipTopicName } from '../../utils/topicUtils';
import './FilterPanel.css';

// Define SunIcon component
const SunIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"></circle>
    <line x1="12" y1="1" x2="12" y2="3"></line>
    <line x1="12" y1="21" x2="12" y2="23"></line>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
    <line x1="1" y1="12" x2="3" y2="12"></line>
    <line x1="21" y1="12" x2="23" y2="12"></line>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
  </svg>
);

// Define MoonIcon component
const MoonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
  </svg>
);

// Multi-select dropdown component with search
// Props:
//   options: full list of all possible options (never filtered away)
//   availableValues: Set of values that exist in current filtered data (for dimming)
//   selected: array of currently selected values
//   totalCount: total number of possible options (for accurate "X of Y" display)
const MultiSelectDropdown = ({ id, label, options, availableValues, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus the search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = (value) => {
    let newSelected = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    // If all options are now selected, reset to [] (no filter = include everything)
    if (newSelected.length >= options.length) {
      newSelected = [];
    }
    onChange(newSelected);
  };

  // Select All = always reset to [] (no filter = include everything)
  // Clear = also reset to [] 
  // This ensures "no filter" and "all selected" always behave identically
  const handleSelectAll = () => {
    onChange([]);
  };

  // Sort options: available first, then unavailable
  const availSet = availableValues || new Set(options.map(o => o.value));
  const sortedOptions = [...options].sort((a, b) => {
    const aAvail = availSet.has(a.value) ? 0 : 1;
    const bAvail = availSet.has(b.value) ? 0 : 1;
    return aAvail - bAvail;
  });

  // Filter options based on search term
  const filteredOptions = sortedOptions.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Display text logic:
  // [] = no filter applied = show placeholder (meaning all data included)
  // N selected where N < total = "N of total filtered"
  const totalCount = options.length;
  let displayText;
  if (selected.length === 0) {
    displayText = placeholder; // "All Industries", "All Topics", etc.
  } else {
    displayText = `${selected.length} of ${totalCount} selected`;
  }

  // For the "Select All / Clear" checkbox:
  // [] = all included (checked), some selected = indeterminate
  const noFilterApplied = selected.length === 0;
  const someSelected = selected.length > 0;

  return (
    <div className="multi-select-dropdown" ref={dropdownRef}>
      <label htmlFor={id}>{label}</label>
      <button 
        type="button" 
        className={`multi-select-trigger ${selected.length > 0 ? 'has-selection' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <span>{displayText}</span>
        <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      {isOpen && (
        <div className="multi-select-options">
          <div className="multi-select-search">
            <input
              ref={searchInputRef}
              type="text"
              className="multi-select-search-input"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          {!searchTerm && (
            <label className="multi-select-option select-all">
              <input
                type="checkbox"
                checked={noFilterApplied}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={handleSelectAll}
              />
              <span>{someSelected ? 'Clear filters' : 'All included'}</span>
            </label>
          )}
          {filteredOptions.length === 0 ? (
            <div className="multi-select-no-results">No matches found</div>
          ) : (
            filteredOptions.map(option => {
              const isAvailable = availSet.has(option.value);
              return (
                <label 
                  key={option.value} 
                  className={`multi-select-option ${!isAvailable ? 'option-unavailable' : ''}`}
                  title={!isAvailable ? 'No results with current filters' : ''}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(option.value)}
                    onChange={() => handleToggle(option.value)}
                  />
                  <span>{option.label}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

function FilterPanel({ 
  filters, 
  setFilters, 
  onRunAnalysis,
  loading,
  analysisStatus,
  theme, 
  toggleTheme, 
  themeColors,
  availableDatePoints,
  availableIndustries = [],
  availableTopics = [],
  availableThemes = [],
  availableOrganisations = [],
  availableCollaborationTypes = [],
  availableStatuses = [],
  dynamicFilterOptions = {},
  schemaConfig = null,
  onSignOut,
  onHelpSection
}) {
  const [isStatusVisible, setIsStatusVisible] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Temporary slider values while dragging (before committing to filters)
  const [tempTimeFrame, setTempTimeFrame] = useState(null);
  const collapseTimerRef = useRef(null);
  const initialCollapseTimerRef = useRef(null);
  const hasUserHoveredRef = useRef(false);

  // Check for mobile/small screen on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 900);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // On mobile, always keep panel expanded (or use different logic)
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(false); // Never auto-collapse on mobile
    }
  }, [isMobile]);

  // Auto-collapse once after initial load (desktop only).
  // If user hovers before timeout, skip this behavior.
  useEffect(() => {
    if (isMobile || hasUserHoveredRef.current) {
      return;
    }

    if (initialCollapseTimerRef.current) {
      clearTimeout(initialCollapseTimerRef.current);
    }

    initialCollapseTimerRef.current = setTimeout(() => {
      if (!hasUserHoveredRef.current) {
        setIsCollapsed(true);
      }
      initialCollapseTimerRef.current = null;
    }, 1000);

    return () => {
      if (initialCollapseTimerRef.current) {
        clearTimeout(initialCollapseTimerRef.current);
        initialCollapseTimerRef.current = null;
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (analysisStatus) {
      setIsStatusVisible(true); // Show status when analysisStatus is updated
    }
  }, [analysisStatus]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
      }
      if (initialCollapseTimerRef.current) {
        clearTimeout(initialCollapseTimerRef.current);
      }
    };
  }, []);

  // Handle mouse enter/leave for collapse behavior with delay (desktop only)
  const handleMouseEnter = () => {
    if (isMobile) return; // Disable on mobile

    hasUserHoveredRef.current = true;

    if (initialCollapseTimerRef.current) {
      clearTimeout(initialCollapseTimerRef.current);
      initialCollapseTimerRef.current = null;
    }

    // Cancel any pending collapse when mouse enters
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    // Expand immediately
    setIsCollapsed(false);
  };

  const handleMouseLeave = () => {
    if (isMobile) return; // Disable on mobile
    // Delay collapse by 2 seconds
    collapseTimerRef.current = setTimeout(() => {
      setIsCollapsed(true);
    }, 1618);
  };

  // Handle touch/click to toggle collapse (for touch devices and as fallback)
  const handleToggleCollapse = (e) => {
    // Only toggle if clicking on the collapse indicator
    if (e.target.closest('.collapse-indicator')) {
      e.preventDefault();
      setIsCollapsed(false);
    }
  };

  // Static filter options (full list)
  const allOrgTypeChoices = [
    { value: 'Company', label: 'Companies' },
    { value: 'University', label: 'Universities' },
    { value: 'Other', label: 'Others' }
  ];

  // Always show the FULL list of options — never hide options from users.
  // Dynamic options are used only to dim/badge unavailable items.
  const { industries: dynIndustries, topics: dynTopics, themes: dynThemes, statuses: dynStatuses, orgTypes: dynOrgTypes, organisations: dynOrganisations } = dynamicFilterOptions;
  
  // Full option lists (always complete)
  const industryChoices = availableIndustries.map(ind => ({ value: ind, label: ind }));
  const topicChoices = availableTopics.map(topic => ({ value: topic, label: clipTopicName(topic) }));
  const themeChoices = availableThemes.map(t => ({ value: t, label: t }));
  const statusChoices = availableStatuses.map(s => ({ value: s, label: s }));
  const orgTypeChoices = allOrgTypeChoices;
  const organisationChoices = availableOrganisations.map(org => ({ value: org, label: org }));

  // Sets of currently available values (for dimming unavailable options)
  const availableIndustrySet = new Set(dynIndustries?.length > 0 ? dynIndustries : availableIndustries);
  const availableTopicSet = new Set(dynTopics?.length > 0 ? dynTopics : availableTopics);
  const availableThemeSet = new Set(dynThemes?.length > 0 ? dynThemes : availableThemes);
  const availableStatusSet = new Set(dynStatuses?.length > 0 ? dynStatuses : availableStatuses);
  const availableOrgTypeSet = new Set(dynOrgTypes?.length > 0 ? dynOrgTypes : allOrgTypeChoices.map(t => t.value));
  const availableOrgSet = new Set(dynOrganisations?.length > 0 ? dynOrganisations : availableOrganisations);

  // Schema-driven facet visibility. When schemaConfig is null (loading) or
  // missing, default to showing every facet (preserves MaxIV behaviour).
  const facetSet = schemaConfig?.facets ? new Set(schemaConfig.facets) : null;
  const showFacet = (name) => !facetSet || facetSet.has(name);
  const labels = schemaConfig?.labels || {};
  const labelFor = (facet, fallback) => labels[facet] || fallback;
  // Pluralise a label without breaking already-plural ("Topics") or -y words
  // ("Industry" → "Industries"). Avoids artefacts like "Topicss".
  const plural = (w) => {
    if (!w) return w;
    if (/s$/i.test(w)) return w;
    if (/[^aeiou]y$/i.test(w)) return `${w.slice(0, -1)}ies`;
    return `${w}s`;
  };

  // Reset all filters to default
  const handleResetFilters = () => {
    // Compute the default timeFrame immediately so the analysis doesn't run
    // with an empty timeFrame (which the backend treats as "no filter").
    let defaultTimeFrame = [];
    if (currentDatePoints.length > 0) {
      const years = [...new Set(currentDatePoints.map(dp => dp.replace(/^(Spring|Fall)/, '')))];
      for (let i = years.length - 1; i >= 0; i--) {
        const yr = years[i];
        const springIdx = currentDatePoints.indexOf('Spring' + yr);
        const fallIdx = currentDatePoints.indexOf('Fall' + yr);
        if (springIdx !== -1 && fallIdx !== -1) {
          defaultTimeFrame = ['Spring' + yr, 'Fall' + yr];
          break;
        }
      }
      if (defaultTimeFrame.length === 0) {
        const last = currentDatePoints[currentDatePoints.length - 1];
        defaultTimeFrame = [last, last];
      }
    }
    setFilters({
      industry: [],
      topics: [],
      status: [],
      timeFrame: defaultTimeFrame,
      orgType: [],
      organisation: [],
      collaborationType: [],
      unspecifiedMode: 'on'
    });
    setTempTimeFrame(null);
  };

  // Use availableDatePoints prop; default to empty array if not provided or empty
  const currentDatePoints = (availableDatePoints && availableDatePoints.length > 0) ? availableDatePoints : [];
  const slidersDisabled = currentDatePoints.length === 0;

  // Logic for determining the time frame to display and operate with
  let resolvedTimeFrame;
  if (slidersDisabled) {
    resolvedTimeFrame = ['', '']; // Placeholders when no dates are available
  } else {
    // Check if filters.timeFrame is valid within the current available date points
    const [filterStart, filterEnd] = Array.isArray(filters.timeFrame) && filters.timeFrame.length === 2 ? filters.timeFrame : [null, null];
    const startIdxInCurrent = currentDatePoints.indexOf(filterStart);
    const endIdxInCurrent = currentDatePoints.indexOf(filterEnd);

    if (startIdxInCurrent !== -1 && endIdxInCurrent !== -1 && startIdxInCurrent <= endIdxInCurrent) {
      resolvedTimeFrame = [filterStart, filterEnd]; // Use valid filters.timeFrame
    } else {
      // filters.timeFrame is invalid or not set for current availableDatePoints,
      // so default to the full range of available points.
      resolvedTimeFrame = [currentDatePoints[0], currentDatePoints[currentDatePoints.length - 1]];
    }
  }
  // This currentTimeFrame is what the component internally decides to work with for its display and slider logic.
  const currentTimeFrame = resolvedTimeFrame;
  
  // Use temp values during drag, otherwise use current values
  const activeTimeFrame = tempTimeFrame || currentTimeFrame;

  // Calculate display indices for sliders based on the active time frame
  let displayStartIndex = 0;
  // Default for disabled or empty case, or if last point is the only point.
  let displayEndIndex = slidersDisabled ? 0 : (currentDatePoints.length > 0 ? currentDatePoints.length - 1 : 0) ;

  if (!slidersDisabled) {
    const startIndexInResolved = currentDatePoints.indexOf(activeTimeFrame[0]);
    const endIndexInResolved = currentDatePoints.indexOf(activeTimeFrame[1]);

    displayStartIndex = (startIndexInResolved !== -1) ? startIndexInResolved : 0;
    displayEndIndex = (endIndexInResolved !== -1) ? endIndexInResolved : (currentDatePoints.length - 1);
    
    // Ensure endIndex is not less than startIndex if specific values were resolved
    if (displayEndIndex < displayStartIndex) {
        displayEndIndex = displayStartIndex;
    }
  }

  // --- Dual Range Slider Logic ---
  const trackRef = useRef(null);
  const draggingRef = useRef(null); // 'start' | 'end' | null

  const getIndexFromPosition = useCallback((clientX) => {
    if (!trackRef.current || currentDatePoints.length === 0) return 0;
    const rect = trackRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const maxIdx = currentDatePoints.length - 1;
    return Math.round(percent * maxIdx);
  }, [currentDatePoints]);

  const handlePointerDown = useCallback((thumb) => (e) => {
    if (slidersDisabled) return;
    e.preventDefault();
    draggingRef.current = thumb;
    
    const onMove = (moveEvent) => {
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const newIdx = getIndexFromPosition(clientX);
      const base = tempTimeFrame || currentTimeFrame;
      let sIdx = currentDatePoints.indexOf(base[0]);
      let eIdx = currentDatePoints.indexOf(base[1]);
      if (sIdx === -1) sIdx = 0;
      if (eIdx === -1) eIdx = currentDatePoints.length - 1;

      if (draggingRef.current === 'start') {
        sIdx = newIdx;
        // If dragged past end, push end along
        if (sIdx > eIdx) eIdx = sIdx;
      } else {
        eIdx = newIdx;
        // If dragged before start, push start along
        if (eIdx < sIdx) sIdx = eIdx;
      }
      setTempTimeFrame([currentDatePoints[sIdx], currentDatePoints[eIdx]]);
    };
    
    const onUp = () => {
      draggingRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      // Commit on release
      setTempTimeFrame(prev => {
        if (prev) {
          setFilters(f => ({ ...f, timeFrame: prev }));
        }
        return null;
      });
    };
    
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onUp);
  }, [slidersDisabled, currentDatePoints, currentTimeFrame, tempTimeFrame, getIndexFromPosition, setFilters]);

  // Compute percentages for thumb positions and active range
  const maxIdx = currentDatePoints.length > 1 ? currentDatePoints.length - 1 : 0;
  const startPercent = maxIdx > 0 ? (displayStartIndex / maxIdx) * 100 : 0;
  const endPercent = maxIdx > 0 ? (displayEndIndex / maxIdx) * 100 : 100;

  return (
    <div 
      className={`filter-panel ${isCollapsed ? 'collapsed' : ''} ${isMobile ? 'mobile' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleToggleCollapse}
      onTouchStart={handleToggleCollapse}
    >
      {/* Collapse indicator - visible when collapsed */}
      <div className="collapse-indicator" role="button" aria-label="Expand filter panel">
        <img src="/TA_Logo_2colours.png" alt="Tiller Alpha" />
        <span className="expand-hint">Tap to expand</span>
      </div>

      <div className="filter-panel-header">
        <div className="header-content">
          <img src="/TA_Logo_2colours.png" alt="Tiller Alpha Logo" className="filter-panel-logo" />
          <div className="filter-panel-title-text">
            <h1>nexus</h1>
            <p>lead with evidence</p>
          </div>
        </div>
        {/* Theme toggle button moved here, as a sibling to header-content */}
        <button 
          onClick={toggleTheme} 
          className="theme-toggle-button" 
          aria-label="Toggle theme"
          title={theme === 'autumn' ? "Switch to Dark Theme" : "Switch to Light Theme"}
        >
          {theme === 'autumn' ? <MoonIcon /> : <SunIcon />}
        </button>
      </div>

      {/* Combined Filters Card: Time, Collaborations, Status, Org Type */}
      <div className="combined-filters-card">
        <div className="filters-section-header">
          <h2>Data filters</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {onHelpSection && (
              <button
                className="help-info-icon"
                onClick={() => onHelpSection('dataFilter')}
                data-tooltip="" title="Learn about filters"
              >
                ⓘ
              </button>
            )}
            <button
              type="button"
              className="reset-filters-button"
              onClick={handleResetFilters}
              title="Reset all filters to default"
            >
              ↺ Reset
            </button>
          </div>
        </div>
        
        {/* Time Filter - Full Width */}
        <div className="filter-group time-frame-filter-group">
          <h3 title="Filters by active period — a project is shown if its start–end span overlaps the selected range, so multi-year projects appear in every year they were running.">Time ⓘ</h3>
          <div
            className={`dual-range-track-wrapper ${slidersDisabled ? 'disabled' : ''}`}
          >
            <div className="dual-range-track-inner" ref={trackRef}>
              {/* Static edge labels */}
              {!slidersDisabled && currentDatePoints.length > 0 && (
                <>
                  <span className="dual-range-edge-label edge-start">{currentDatePoints[0]}</span>
                  <span className="dual-range-edge-label edge-end">{currentDatePoints[currentDatePoints.length - 1]}</span>
                </>
              )}
              {/* Track background */}
              <div className="dual-range-track" />
              {/* Active range */}
              <div
                className="dual-range-active"
                style={{
                  left: `${startPercent}%`,
                  width: `${endPercent - startPercent}%`
                }}
              />
              {/* Small dot markers for each date point */}
              {!slidersDisabled && currentDatePoints.map((dp, i) => {
                const pct = maxIdx > 0 ? (i / maxIdx) * 100 : 50;
                const inRange = i >= displayStartIndex && i <= displayEndIndex;
                return (
                  <div
                    key={dp}
                    className={`dual-range-dot ${inRange ? 'in-range' : ''}`}
                    style={{ left: `${pct}%` }}
                  />
                );
              })}
              {/* Start thumb */}
              <div
                className="dual-range-thumb thumb-start"
                style={{ left: `${startPercent}%` }}
                onMouseDown={handlePointerDown('start')}
                onTouchStart={handlePointerDown('start')}
                role="slider"
                aria-label="Time frame start"
                aria-valuemin={0}
                aria-valuemax={maxIdx}
                aria-valuenow={displayStartIndex}
                tabIndex={slidersDisabled ? -1 : 0}
              >
                <span className="thumb-label">{slidersDisabled ? '' : activeTimeFrame[0]}</span>
              </div>
              {/* End thumb */}
              <div
                className={`dual-range-thumb thumb-end ${displayStartIndex === displayEndIndex ? 'stacked' : ''}`}
                style={{ left: `${endPercent}%` }}
                onMouseDown={handlePointerDown('end')}
                onTouchStart={handlePointerDown('end')}
                role="slider"
                aria-label="Time frame end"
                aria-valuemin={0}
                aria-valuemax={maxIdx}
                aria-valuenow={displayEndIndex}
                tabIndex={slidersDisabled ? -1 : 0}
              >
                <span className="thumb-label">{slidersDisabled ? '' : activeTimeFrame[1]}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Other Filters - Grid Layout */}
        <div className="filters-grid">
          {/* Industry Filter Group - Multi-select */}
          {showFacet('industry') && (
          <MultiSelectDropdown
            id="industry"
            label={`${labelFor('industry', 'Industry')}:`}
            options={industryChoices}
            availableValues={availableIndustrySet}
            selected={filters.industry || []}
            onChange={(selected) => setFilters({ ...filters, industry: selected })}
            placeholder={`All ${plural(labelFor('industry', 'Industries'))}`}
          />
          )}
          
          {/* Topics Filter Group - Multi-select */}
          {showFacet('topics') && (
          <MultiSelectDropdown
            id="topics"
            label={`${labelFor('topic', 'Topic')}:`}
            options={topicChoices}
            availableValues={availableTopicSet}
            selected={filters.topics || []}
            onChange={(selected) => setFilters({ ...filters, topics: selected })}
            placeholder={`All ${plural(labelFor('topic', 'Topic'))}`}
          />
          )}

          {/* Hackathon themes Filter Group - Multi-select */}
          {showFacet('themes') && (
          <MultiSelectDropdown
            id="themes"
            label={`${labelFor('themes', 'Hackathon themes')}:`}
            options={themeChoices}
            availableValues={availableThemeSet}
            selected={filters.themes || []}
            onChange={(selected) => setFilters({ ...filters, themes: selected })}
            placeholder={`All ${labelFor('themes', 'themes')}`}
          />
          )}
          
          {/* Status Filter Group - Multi-select */}
          {showFacet('status') && (
          <MultiSelectDropdown
            id="status"
            label="Status:"
            options={statusChoices}
            availableValues={availableStatusSet}
            selected={filters.status || []}
            onChange={(selected) => setFilters({ ...filters, status: selected })}
            placeholder="All Statuses"
          />
          )}
          
          {/* Org Type Filter Group - Multi-select */}
          {showFacet('orgType') && (
          <MultiSelectDropdown
            id="orgType"
            label="Org Type:"
            options={orgTypeChoices}
            availableValues={availableOrgTypeSet}
            selected={filters.orgType || []}
            onChange={(selected) => setFilters({ ...filters, orgType: selected })}
            placeholder="All Types"
          />
          )}
          
          {/* Organisation Filter Group - Multi-select */}
          {showFacet('organisation') && (
          <MultiSelectDropdown
            id="organisation"
            label="Organisation:"
            options={organisationChoices}
            availableValues={availableOrgSet}
            selected={filters.organisation || []}
            onChange={(selected) => setFilters({ ...filters, organisation: selected })}
            placeholder="All Organisations"
          />
          )}
        </div>
      </div>

      {/* Strategic Fine-Tuning Card (MaxIV-specific; gated by the
          'collaboration' facet declared in the active schema) */}
      {showFacet('collaboration') && (
      <div className="strategic-fine-tuning-card">
        <div className="filters-section-header">
          <h2>Strategic Fine-Tuning</h2>
          {onHelpSection && (
            <button
              className="help-info-icon"
              onClick={() => onHelpSection('strategicFineTuning')}
              data-tooltip="" title="Learn about fine-tuning"
            >
              ⓘ
            </button>
          )}
        </div>

        {/* Unspecified Tri-State Toggle */}
        <div className="fine-tuning-group">
          <label className="fine-tuning-label">Unspecified</label>
          <div className="tri-state-toggle">
            {['on', 'off', 'only'].map((mode) => (
              <button
                key={mode}
                type="button"
                className={`tri-state-btn ${(filters.unspecifiedMode || 'on') === mode ? 'active' : ''}`}
                onClick={() => {
                  const newFilters = { ...filters, unspecifiedMode: mode };
                  // When switching to OFF, remove 'No Industrial Connection' from collaborationType
                  // since those proposals are excluded anyway
                  if (mode === 'off') {
                    const collab = newFilters.collaborationType || [];
                    if (collab.includes('No Industrial Connection')) {
                      newFilters.collaborationType = collab.filter(v => v !== 'No Industrial Connection');
                      // If that was the only selection, reset to [] (all)
                      if (newFilters.collaborationType.length === 0 || newFilters.collaborationType.length === 2) {
                        newFilters.collaborationType = [];
                      }
                    }
                  }
                  setFilters(newFilters);
                }}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
          <span className="fine-tuning-hint">
            {(filters.unspecifiedMode || 'on') === 'on' && 'All proposals included'}
            {filters.unspecifiedMode === 'off' && 'Only proposals with described collaborations'}
            {filters.unspecifiedMode === 'only' && 'Only vague/unspecified proposals'}
          </span>
        </div>

        {/* Collaboration Type Checkboxes */}
        <div className="fine-tuning-group">
          <label className="fine-tuning-label">Collaboration Type</label>
          <div className="collab-type-checkboxes">
            {[
              { value: 'actual', label: 'Actual' },
              { value: 'potential', label: 'Potential' },
              { value: 'No Industrial Connection', label: 'No Connection' }
            ].map((type) => {
              const currentSelection = filters.collaborationType || [];
              const isChecked = currentSelection.length === 0 || currentSelection.includes(type.value);
              // Disable "No Connection" when Unspecified is OFF, since those proposals
              // are excluded (they have no named entity).
              const isDisabled = type.value === 'No Industrial Connection' && filters.unspecifiedMode === 'off';
              return (
                <label key={type.value} className={`collab-type-option${isDisabled ? ' disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={isDisabled ? false : isChecked}
                    disabled={isDisabled}
                    onChange={() => {
                      if (isDisabled) return;
                      let newSelection;
                      if (currentSelection.length === 0) {
                        // First click: deselect this one (select all others)
                        newSelection = ['actual', 'potential', 'No Industrial Connection'].filter(v => v !== type.value);
                      } else if (isChecked) {
                        // Currently checked, uncheck it
                        newSelection = currentSelection.filter(v => v !== type.value);
                      } else {
                        // Currently unchecked, check it
                        newSelection = [...currentSelection, type.value];
                      }
                      // If all selected, reset to empty (meaning all)
                      if (newSelection.length === 3) newSelection = [];
                      setFilters({ ...filters, collaborationType: newSelection });
                    }}
                  />
                  <span>{type.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </div>
      )}


      {/* Sign Out */}
      {onSignOut && (
        <div className="filter-panel-signout">
          <button className="filter-panel-signout-btn" onClick={onSignOut} title="Sign out">
            Sign Out
          </button>
        </div>
      )}
      
      {/* Floating Analysis status indicator */}
      {analysisStatus && isStatusVisible && (
        <div className="floating-analysis-status">
          <span>{analysisStatus}</span>
          <button 
            onClick={() => setIsStatusVisible(false)} 
            className="close-status-button"
            aria-label="Close status message"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

export default FilterPanel;